import type { Node, Path, Range } from 'slate'
import { Node as SlateNode, Range as SlateRange } from 'slate'
import { ReactEditor, useSlateStatic } from 'slate-react'
import { useSyncExternalStore } from 'react'
import { Editor } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { getSelectionStore } from './selection-store.js'

/**
 * Pure helper: does the given block's range intersect the current selection?
 *
 * Mirrors the semantics of `slate-react`'s `useSelected()`:
 * - null selection → false
 * - collapsed caret → true iff the caret falls inside the block
 * - expanded selection → true iff the selection and block share any point
 *
 * Try/catch around the whole body matches `isInsideSelectionRangeByPath` —
 * during teardown / normalization races the `Range` can transiently be
 * malformed (GH #1204 / Sentry TUNASONG-VK). The hook layer must not throw.
 */
export const intersectsSelection = (editor: TunaEditor, selection: Range | null, path: Path): boolean => {
  if (!selection) {
    return false
  }
  try {
    // `Editor.range` on a path that no longer resolves throws, and Slate builds that
    // error message with `Scrubber.stringify(root)` — a JSON.stringify of the *entire*
    // document. `notify()` runs inside `onChange`, before slate-react refreshes
    // NODE_TO_INDEX, so `findPath` hands us stale paths on every block split; a single
    // Enter burned ~140ms of a ~180ms task serialising the document for errors nobody
    // reads. `Node.has` is an O(path depth) walk that resolves the same question
    // without the throw. It stays inside the try: a malformed `path` still trips it.
    if (!SlateNode.has(editor, path)) {
      return false
    }
    const blockRange = Editor.range(editor, path)
    return SlateRange.intersection(selection, blockRange) !== null
  } catch {
    return false
  }
}

/**
 * Pure helper: is the current selection collapsed (caret only, no expanded range)?
 *
 * `null` selection is treated as "collapsed" — `shouldShowChordPopup` reads this
 * as "there's no expanded range across multiple chords" (the bug #1171 it guards
 * against). The popup separately checks `selected`/`focused`, so null-selection
 * still hides the popup overall via those gates.
 */
export const isSelectionCollapsed = (selection: Range | null): boolean => {
  if (!selection) {
    return true
  }
  try {
    return SlateRange.isCollapsed(selection)
  } catch {
    return false
  }
}

/**
 * Returns true when this node's range intersects the current selection.
 *
 * Drop-in replacement for `slate-react`'s `useSelected()`. The difference is the
 * subscription: `useSelected()` registers a selector with `useSlateSelector`,
 * which re-runs every selector on every editor change (so N consumers ⇒ N
 * `findPath` walks per keystroke). This hook reads from the shared
 * `SelectionStore`, which computes each node's scalar at most once per editor
 * change (cached by node) and gates re-renders via `Object.is` on the boolean.
 *
 * For a single-cursor keystroke:
 * - the block under the cursor: true → true → no re-render
 * - every other block: false → false → no re-render
 *
 * Caller passes the node directly; no `usePath()` needed.
 */
export const useNodeSelected = (node: Node): boolean => {
  const editor = useSlateStatic() as unknown as TunaEditor
  const store = getSelectionStore(editor)

  const getSnapshot = () =>
    store.getOrComputeScalar(node, n => {
      const path = ReactEditor.findPath(editor as unknown as ReactEditor, n)
      return intersectsSelection(editor, editor.selection ?? null, path)
    })

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)
}

/**
 * Returns true when the current selection is collapsed (or absent). Re-renders
 * only when this boolean flips — typing within a single line keeps it `true`
 * across every keystroke, so consumers using this for popup gating do not
 * re-render on cursor motion.
 *
 * The store-backed subscription means a single per-editor onChange listener
 * drives all consumers, not N selector evaluations.
 */
export const useIsSelectionCollapsed = (): boolean => {
  const editor = useSlateStatic() as unknown as TunaEditor
  const store = getSelectionStore(editor)

  // Use a singleton sentinel as the cache key so this scalar is cached
  // exactly once per editor per version (it doesn't depend on a node).
  const getSnapshot = () => store.getOrComputeScalar(collapsedKey, () => isSelectionCollapsed(editor.selection ?? null))

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)
}

/**
 * Sentinel WeakMap key for the editor-wide "is selection collapsed?" scalar.
 * Any stable object reference works; we use a frozen object so it isn't
 * mistaken for a Slate Node anywhere else.
 */
const collapsedKey: Node = Object.freeze({ __selectionCollapsedSentinel: true }) as unknown as Node
